/** The job behind a release or a reopen of a released tenant (tenant retirement CONTRACT §10.2,
 *  §10.5): which request's job to show, whether it runs (the Lifecycle tab polls while it does),
 *  and its steps with their progress, from the request's `lifecycle.release` /
 *  `lifecycle.restore` — `step` is the next step to run (`done` once finished), `results` each
 *  finished step's counts. The database is the job's progress store, so a retry resumes there. */
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { Box, CircularProgress, Typography } from '@mui/material';

import type { TenantLifecycleState } from '@/api/tenantLifecycle';
import type { TenantRequest } from '@/api/tenantRequests';

/** How often the Lifecycle tab re-reads the state while a job runs. */
export const LIFECYCLE_POLL_MS = 3000;
/** The longest wait between two reads while reads keep failing (the period doubles per failure). */
export const LIFECYCLE_POLL_MAX_MS = 30_000;
/** How long the tab follows a job on its own before it stops and offers a Refresh (a job whose
 *  worker died stays applying for ever; a big release can take hours). */
export const LIFECYCLE_POLL_CEILING_MS = 10 * 60_000;

/** Reads that failed in a row while following a job; reset by a read that succeeds. */
export interface PollFailures {
  /** The `errorUpdatedAt` last counted. */
  seen: number;
  count: number;
}

/** The wait before the next read: the period, doubled per failure in a row, at most
 *  `LIFECYCLE_POLL_MAX_MS` (never less than the period itself). */
export function jobPollDelay(pollMs: number, failures: number): number {
  return Math.min(pollMs * 2 ** failures, Math.max(pollMs, LIFECYCLE_POLL_MAX_MS));
}

/** `refetchInterval` while following a job: false once no job runs; else the period, backed off
 *  while reads fail. `failures` is updated from the query's own error / data timestamps. */
export function jobRefetchInterval(
  query: { data?: TenantLifecycleState; dataUpdatedAt: number; errorUpdatedAt: number },
  failures: PollFailures,
  pollMs: number,
): number | false {
  if (!isJobRunning(query.data)) {
    failures.count = 0;
    return false;
  }
  if (query.dataUpdatedAt >= query.errorUpdatedAt) {
    failures.count = 0;
  } else if (query.errorUpdatedAt !== failures.seen) {
    failures.seen = query.errorUpdatedAt;
    failures.count += 1;
  }
  return jobPollDelay(pollMs, failures.count);
}

/** How the tab follows a running job, for the Job row: reads failing (backed off), or stopped at
 *  the ceiling with a way to follow it again. */
export interface JobFollow {
  failing: boolean;
  stopped: boolean;
  onResume: () => void;
}

export const RELEASE_STEPS = ['rekey', 'rename', 'purge', 'revoke_keys', 'finish'] as const;
export const RESTORE_STEPS = ['rename', 'restore_ext_ids', 'purge', 'finish'] as const;

const RELEASE_STEP_LABEL: Record<string, string> = {
  rekey: 'Re-key the imported ids',
  rename: 'Rename the code to its placeholder',
  purge: 'Purge what is stored under the code',
  revoke_keys: 'Revoke the API keys',
  finish: 'Finish',
};

const RESTORE_STEP_LABEL: Record<string, string> = {
  rename: 'Rename the tenant back to its code',
  restore_ext_ids: 'Restore the imported ids',
  purge: 'Purge what is stored under the placeholder',
  finish: 'Reopen the tenant',
};

export interface LifecycleJob {
  kind: 'release' | 'restore';
  /** The next step to run; `done` once finished; null when not recorded yet. */
  step: string | null;
  results: Record<string, unknown>;
  steps: readonly string[];
}

const asRecord = (v: unknown): Record<string, unknown> =>
  v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {};

/** The job of a release / reopen request, or null when it has none (a close, a reopen of a closed
 *  tenant, a request not approved yet). */
export function jobOf(request: TenantRequest): LifecycleJob | null {
  const progress =
    request.kind === 'release'
      ? request.lifecycle?.release
      : request.kind === 'reopen'
        ? request.lifecycle?.restore
        : null;
  if (!progress) return null;
  const step = typeof progress.step === 'string' ? progress.step : null;
  return {
    kind: request.kind === 'release' ? 'release' : 'restore',
    step,
    results: asRecord(progress.results),
    steps: request.kind === 'release' ? RELEASE_STEPS : RESTORE_STEPS,
  };
}

export function stepLabel(job: LifecycleJob, step: string): string {
  return (job.kind === 'release' ? RELEASE_STEP_LABEL : RESTORE_STEP_LABEL)[step] ?? step;
}

/** The request applying now, approved a moment ago, or (while the tenant is still releasing or
 *  restoring) the newest job request — a failed one waits there for its retry. */
export function currentJobRequest(state: TenantLifecycleState): TenantRequest | null {
  const open = state.open_request;
  if (open && (open.status === 'applying' || open.status === 'approved') && jobOf(open)) return open;
  if (state.lifecycle === 'releasing' || state.lifecycle === 'restoring') {
    return state.recent_requests.find((r) => jobOf(r) != null) ?? null;
  }
  return null;
}

/** An approved request is being applied: re-read until it is applied or failed. */
export function isJobRunning(state: TenantLifecycleState | undefined): boolean {
  const status = state?.open_request?.status;
  return status === 'applying' || status === 'approved';
}

const sum = (v: unknown): number =>
  Object.values(asRecord(v)).reduce<number>((n, x) => n + (typeof x === 'number' ? x : 0), 0);

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** A finished release step's counts in a few words, or null. */
function releaseStepSummary(step: string, results: Record<string, unknown>): string | null {
  const r = asRecord(results[step]);
  switch (step) {
    case 'rekey': {
      const own = sum(r.ext_ids);
      const twin = sum(r.twin_ext_ids);
      return `${plural(own, 'id')} re-keyed${twin ? `, ${twin} of the sandbox` : ''}.`;
    }
    case 'rename':
      return typeof r.production_key_entries === 'number' && r.production_key_entries > 0
        ? `${plural(r.production_key_entries, 'production-key entry', 'production-key entries')} renamed.`
        : null;
    case 'purge': {
      const done = [r.code_state, r.twin_code_state].reduce<number>(
        (n, families) =>
          n +
          Object.values(asRecord(families)).reduce<number>(
            (m, f) => m + (typeof asRecord(f).done === 'number' ? (asRecord(f).done as number) : 0),
            0,
          ),
        0,
      );
      return `${plural(done, 'key or object', 'keys or objects')} purged or moved.`;
    }
    case 'revoke_keys': {
      const keys = typeof r.api_keys_revoked === 'number' ? r.api_keys_revoked : 0;
      const links = typeof r.connections_unlinked === 'number' ? r.connections_unlinked : 0;
      return `${plural(keys, 'API key')} revoked${links ? `, ${plural(links, 'connection')} unlinked` : ''}.`;
    }
    default:
      return null;
  }
}

type StepState = 'done' | 'running' | 'failed' | 'todo';

/** The job's steps, each done / running (or failed) / still to run. */
export function LifecycleJobSteps({ request, job }: { request: TenantRequest; job: LifecycleJob }) {
  const finished = job.step === 'done';
  const at = job.step == null ? -1 : job.steps.indexOf(job.step);
  const stateOf = (index: number): StepState => {
    if (finished || (at >= 0 && index < at)) return 'done';
    if (index === at) return request.status === 'failed' ? 'failed' : 'running';
    return 'todo';
  };
  return (
    <Box
      component="ol"
      aria-label="Job steps"
      sx={{ m: 0, mt: 0.75, p: 0, listStyle: 'none', display: 'grid', gap: 0.5 }}
    >
      {job.steps.map((step, index) => {
        const s = stateOf(index);
        const summary =
          s === 'done' && job.kind === 'release' ? releaseStepSummary(step, job.results) : null;
        return (
          <Box
            component="li"
            key={step}
            data-step={step}
            data-step-state={s}
            sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}
          >
            <Box sx={{ display: 'flex', mt: '2px' }} aria-hidden>
              {s === 'done' && <CheckCircleOutlineIcon sx={{ fontSize: 16, color: 'success.main' }} />}
              {s === 'running' && <CircularProgress size={14} sx={{ m: '1px' }} />}
              {s === 'failed' && <ErrorOutlineIcon sx={{ fontSize: 16, color: 'error.main' }} />}
              {s === 'todo' && <RadioButtonUncheckedIcon sx={{ fontSize: 16, color: 'text.disabled' }} />}
            </Box>
            <Typography
              variant="body2"
              component="span"
              color={s === 'todo' ? 'text.disabled' : s === 'failed' ? 'error.main' : 'text.primary'}
            >
              {stepLabel(job, step)}
              {s === 'running' && ' — running'}
              {s === 'failed' && ' — failed here'}
              {summary && (
                <Box component="span" sx={{ color: 'text.secondary' }}>
                  {' · '}
                  {summary}
                </Box>
              )}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}
